import { belagGeprueft } from '../../lib/surface'
import { TULPEN_NOTIZ_ZEICHEN_MAX } from '../../lib/tulip'
import type { SurfaceReport, Tulip } from '../../lib/types'

/**
 * Was der Tulpenvorschlag über sich selbst sagt - in Sätzen und in Notizen.
 *
 * Eigenes Modul und nicht in der Maske, weil es hier um Aussagen geht und nicht
 * um Darstellung: „auf dieser Strecke gibt es keinen Tunnel“ und „auf dieser
 * Strecke wurde nicht nach Tunneln gesucht“ sind zwei verschiedene Auskünfte,
 * und welche von beiden dasteht, ist eine Frage mit einer richtigen Antwort.
 * Solche Fragen gehören in eine geprüfte Funktion.
 */

/**
 * Ab welchem Anteil abgedeckter Strecke die Tunnelsuche als vollständig gilt.
 *
 * Neunzig Prozent. Das Kachelarchiv ist ein Korridor um die geplante Reise und
 * nicht die halbe Welt; nimmt der Routendienst eine andere Autobahn, liegt ein
 * Teil der Strecke daneben. Gemessen an den sechzehn Etappen reicht das von
 * vollständiger Deckung bis zu vierzehn Prozent auf der Rückfahrt Budapest -
 * Bern. Wer das nicht sagt, behauptet für 1097 Kilometer Alpentransit einen
 * einzigen Tunnel.
 */
export const ABDECKUNG_VOLLSTAENDIG = 0.9

/**
 * Was die vier Quellen zu dieser Strecke beitragen konnten.
 *
 * @param kehren Wurden die Kehren gezählt? Nein bei einer Strecke aus der Zeit
 *   vor der Zählung - dort gibt es keine, weil nie gezählt wurde.
 * @param abdeckung Anteil der Strecke, für den das Kartenarchiv Kacheln hat,
 *   oder null, wenn kein Archiv da ist.
 * @param belag Der Belagsbericht der Strecke, oder undefined, wenn ihn noch
 *   niemand geholt hat. Das ist die vierte Quelle, und sie ist die einzige, die
 *   der Nutzer selbst anstossen muss - deshalb muss besonders deutlich
 *   dastehen, wenn sie fehlt.
 */
export interface Quellen {
  kehren: boolean
  abdeckung: number | null
  belag?: SurfaceReport
}

/** Wurde auf dieser Strecke wirklich nach Tunneln gesucht? */
export function tunnelGesucht({ abdeckung }: Quellen): boolean {
  return abdeckung !== null && abdeckung >= ABDECKUNG_VOLLSTAENDIG
}

/**
 * Der Satz, der bei einer leeren Vorschlagsliste steht.
 *
 * Er darf nur das verneinen, wonach gesucht wurde. Vorher stand dort
 * kategorisch „keine Kehren und keinen Tunnel“, auch wenn gar keine Karte auf
 * dem Server lag - und direkt darunter, in der Herkunftszeile, stand „es wurde
 * also nicht nach Tunneln gesucht“. Zwei Absätze, die sich widersprachen; der
 * obere ist der auffälligere und der falsche.
 *
 * Die Manöver stehen immer zur Verfügung: sie kommen mit der berechneten
 * Strecke, und ohne sie gäbe es diesen Knopf gar nicht. „Keine Stelle, an der
 * man sich verfahren kann“ darf deshalb immer dastehen.
 *
 * Beim Belag ist die Regel dieselbe und der Fall häufiger: er wird eigens
 * nachgeschlagen, und wer den Knopf daneben nie gedrückt hat, hat keine
 * Auskunft - nicht die Auskunft „kein Schotter“.
 */
export function leerSatz(quellen: Quellen): string {
  const teile = ['keine Stelle, an der man sich verfahren kann']
  if (quellen.kehren) teile.push('keine Kehren')
  if (tunnelGesucht(quellen)) teile.push('keinen Tunnel')
  if (belagGeprueft(quellen.belag)) teile.push('keine unbefestigte Stelle')

  const liste =
    teile.length === 1
      ? teile[0]!
      : `${teile.slice(0, -1).join(', ')} und ${teile[teile.length - 1]!}`
  return (
    `Auf dieser Strecke gibt es ${liste}. Was das Roadbook trotzdem gebrauchen kann — ` +
    'Grenzen, Mautstellen, Bahnübergänge, Ortsdurchfahrten —, steht in keiner Quelle und ' +
    'bleibt Handarbeit.'
  )
}

/**
 * Der Satz über die Vorschläge, die schon im Roadbook stehen.
 *
 * Er muss dastehen, weil die Liste sie nicht mehr zeigt: wer eine Etappe
 * durchgearbeitet hat und den Knopf ein zweites Mal drückt, bekäme sonst eine
 * kürzere Liste als beim ersten Mal und keine Auskunft darüber, wohin der Rest
 * verschwunden ist - und würde vermuten, die Suche habe diesmal weniger
 * gefunden. Gefunden hat sie gleich viel; entschieden ist nur schon mehr.
 *
 * @param schon Wie viele Vorschläge bereits als Tulpe im Buch stehen.
 * @param offen Wie viele noch zur Entscheidung stehen. Null heisst: die Liste
 *   ist abgearbeitet, und dann ist das der einzige Satz, der dasteht.
 */
export function schonImBuchSatz(schon: number, offen: number): string {
  if (schon <= 0) return ''

  if (offen > 0) {
    return schon === 1
      ? 'Ein weiterer Vorschlag steht schon im Roadbook und ist hier nicht aufgeführt.'
      : `${String(schon)} weitere Vorschläge stehen schon im Roadbook und sind hier nicht aufgeführt.`
  }
  return schon === 1
    ? 'Der einzige Vorschlag dieser Strecke steht schon im Roadbook.'
    : `Alle ${String(schon)} Vorschläge dieser Strecke stehen schon im Roadbook.`
}

/**
 * Schreibt den Vorbehalt über die Kartendeckung in die übernommenen Tulpen.
 *
 * Der Grund: die Abdeckung ist die eigentliche Auskunft der Tunnelsuche, und
 * sie war bisher flüchtiger Zustand der Maske. Wer die Vorschläge übernahm,
 * bekam die Tunnelzeile ins Buch und den Vorbehalt nicht - auf der Rückfahrt
 * Budapest - Bern also eine Tunnelzeile, hinter der 86 Prozent der Strecke
 * ungeprüft sind. Falsch behauptet wird damit nichts, aber der Kernpunkt der
 * ganzen Funktion steht dann nicht auf dem Papier, und das Papier ist das, was
 * mitfährt.
 *
 * Angehängt wird an die *erste* übernommene Tunnelzeile und nicht an jede: der
 * Vorbehalt gilt der Vollständigkeit der Liste und nicht dem einzelnen Bauwerk.
 * Einmal gelesen genügt; an jeder Zeile wäre er Rauschen.
 *
 * Was hier ausdrücklich nicht gelöst ist: findet die Teilsuche gar keinen
 * Tunnel, gibt es keine Zeile, an der der Vorbehalt stehen könnte. Dafür
 * bräuchte es ein Feld an der Etappe und einen Platz dafür im Druck.
 *
 * @param tulpen Die übernommenen Vorschläge, in Fahrreihenfolge.
 * @param quellen Was gefragt werden konnte.
 * @returns Dieselbe Liste; die betroffene Zeile ist ersetzt, nicht verändert.
 */
export function mitAbdeckungsvermerk(tulpen: readonly Tulip[], quellen: Quellen): Tulip[] {
  const { abdeckung } = quellen
  if (abdeckung === null || abdeckung >= ABDECKUNG_VOLLSTAENDIG) return [...tulpen]

  const i = tulpen.findIndex((t) => t.shape === 'tunnel')
  if (i < 0) return [...tulpen]

  const vermerk = `Karte ${String(Math.round(abdeckung * 100))} % der Strecke`
  const tulpe = tulpen[i]!
  const ganz = tulpe.note ? `${tulpe.note} · ${vermerk}` : vermerk
  const out = [...tulpen]
  // Der Deckel des Eingabefelds gilt auch hier: er ist die Zusage, dass das,
  // was in der Liste steht, im Buch ganz dasteht.
  out[i] = { ...tulpe, note: ganz.slice(0, TULPEN_NOTIZ_ZEICHEN_MAX) }
  return out
}
